// @flow

import * as React from "react";
import { useSelector } from "react-redux";

import { type LinkType, type ImageType } from "../../types/";
import Icon from "../atoms/Icon";
import Image from "../atoms/Image";
import Modal from "../organisms/Modal";
import MediaCarousel from "../molecules/MediaCarousel";
import SectionHeader from "../molecules/SectionHeader";
import getYoutubePreviewImage from "../../utils/getYoutubePreviewImage";

type ImagePropsType = {
    media: {
        image: ImageType,
        youtube_url: LinkType,
        caption?: string,
    },
    index?: number,
    length?: number,
    mode?: string,
    showModal: Function,
    setActive: Function,
};

type PropsType = {
    media?: {
        image: ImageType,
        youtube_url: LinkType,
        caption?: string,
    }[],
    containerClass?: string,
    itemClass: string,
    title?: string,
    description?: string,
    logo?: ImageType,
    mode?: string,
    theme?: string,
};

const MediaGalleryImage = ({
    media,
    index,
    length,
    mode,
    showModal,
    setActive,
}: ImagePropsType): React.Node => {
    const { default_tile_image } = useSelector(
        (state: any): any => state.options
    );
    const previewImage =
        media.image && media.image.url
            ? media.image.url
            : media.youtube_url
            ? getYoutubePreviewImage(media.youtube_url.url)
            : default_tile_image.sizes.medium_large;
    let aspectRatio = "1:1";

    if (mode === "header") {
        if (length === 1) {
            aspectRatio = "3:2";
        }
    } else {
        if (index === 1) {
            aspectRatio = "3:2";
        }
    }

    const bottomBadge = (length, mode) => {
        if (length && length <= 3) {
            return (
                <div className="media-gallery__expand">
                    <Icon name="expand" width="32px" />
                </div>
            );
        }

        if (mode === "header") {
            if (length && length > 3) {
                return (
                    <div className="media-gallery__bottom-badge">
                        Show all {length} photos
                    </div>
                );
            }
        }

        return null;
    };

    const moreOverlay = (index, length) => {
        if (length > 3 && parseInt(index) === 3) {
            return (
                <div className="media-gallery__extend-overlay">
                    +{length - 3}
                </div>
            );
        }
        return null;
    };

    const onClick = (index: number) => {
        showModal(true);
        setActive(index - 1);
    };

    return (
        <div
            className="media-gallery__image"
            onClick={() => index && onClick(index)}
        >
            <Image
                src={previewImage}
                alt={media.caption}
                aspectRatio={aspectRatio}
            />

            {media.youtube_url ? (
                <div className="media-gallery__play">
                    <Icon name="play" width="32px" />
                </div>
            ) : null}
            {length && index === 1 ? bottomBadge(length, mode) : null}
            {length && mode !== "header" ? moreOverlay(index, length) : null}
        </div>
    );
};

const MediaGallery = (props: PropsType): React.Node => {
    const classList = ["media-gallery"];

    const [activeImage, setActiveImage] = React.useState(0);
    const [showModal, setShowModal] = React.useState(false);

    if (props.mode) {
        classList.push(`media-gallery--${props.mode}`);
    }

    if (props.theme) {
        classList.push(`media-gallery--${props.theme}`);
    }

    return (
        <div className={classList.join(" ")}>
            {props.mode !== "header" ? (
                <SectionHeader
                    sectionName="media-gallery"
                    title={props.title}
                    description={props.description}
                />
            ) : null}
            <div className="media-gallery__gallery">
                <div className="media-gallery__main-content">
                    {props.media ? (
                        <React.Fragment>
                            <MediaGalleryImage
                                media={props.media[0]}
                                index={1}
                                mode={props.mode}
                                length={props.media.length}
                                showModal={setShowModal}
                                setActive={setActiveImage}
                            />
                            {props.logo ? (
                                <div className="media-gallery__main-content__logo">
                                    <Image
                                        image={props.logo}
                                        size={"large"}
                                        alt="Club Logo"
                                        aspectRatio="1:1"
                                    />
                                </div>
                            ) : null}
                        </React.Fragment>
                    ) : null}
                </div>
                {props.media && props.media.length > 1 ? (
                    <div className="media-gallery__side-content">
                        {props.media[1] ? (
                            <MediaGalleryImage
                                media={props.media[1]}
                                index={2}
                                showModal={setShowModal}
                                setActive={setActiveImage}
                            />
                        ) : null}
                        {props.media[2] ? (
                            <MediaGalleryImage
                                media={props.media[2]}
                                index={3}
                                mode={props.mode}
                                length={props.media.length}
                                showModal={setShowModal}
                                setActive={setActiveImage}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
            {props.media && props.media.length > 0 ? (
                <Modal
                    className="media-carousel-modal"
                    active={showModal}
                    onClose={() => setShowModal(false)}
                    onOverlayClick={() => setShowModal(false)}
                >
                    <MediaCarousel
                        media={props.media}
                        initialSlide={activeImage}
                    />
                </Modal>
            ) : null}
        </div>
    );
};

MediaGallery.defaultProps = {
    itemClass: "col-xs-4 col-sm-3 col-md-3",
};

export default MediaGallery;
